// Plain-language business rules shown by the "?" help icons (RulesHelp.jsx).
// These are the USER-FACING versions of docs/business-rules/*.md — same rules,
// no code references. WHEN A RULE CHANGES, UPDATE BOTH: the dev doc (with legacy
// file:line + enforcement point) and the wording here.
//
// Topic map (dev doc → topics):
//   orders.md    → orders, overruns
//   quotes.md    → quotes
//   invoices.md  → invoices, predictiveInvoicing, invoicingByMonth
//   details.md   → details
//   status.md    → status
//   progress.md  → progress
//   users.md     → (admin-facing, no "?")
//   workflow.md  → workflow, marketingCalls
//   rdr-reports.md → rdrMain, accounts, healthSafety, qcc, qccReport, dashboard
//   sla.md       → sla, fileLife
//   predefined-reports.md → feedback, importantJobs, strikeRate, jobSchedule,
//                           nonApprovedInvoices, phonebook (+ overruns)
//   (service-defined) → pow, progressClaims, files, jobsLogged, completions

const BUSINESS_RULES = {
  orders: {
    title: "How Orders work",
    intro:
      "Orders are the payable side of the claim: one purchase package per work centre/contractor. " +
      "Committed cost (Contract Given) comes from the contract lines; actual cost (Invoices Received) from contractor invoices.",
    sections: [
      {
        heading: "Auto-create from Quote",
        rules: [
          "Only offered while the job has quote lines and NO orders at all — it's a one-shot starting point; after that, orders are managed by hand.",
          "The button disables while orders are being created. Overlapping requests cannot create a second set for the same job.",
          "Blocked (with the reason shown) while: a priced quote line has no work centre, a Scope of Works note still needs confirmation, or the PC Sums don't match the PC-Sum estimates.",
          "Creates one order per work centre with a contract line per estimate line, including priced heading rows — amounts are the estimate's COST (GPS keeps the margin).",
        ],
      },
      {
        heading: "Orders & contract lines",
        rules: [
          "Every order gets a permanent Order # from the same numbering family as claims and invoices — it's the number to quote when talking about the order. (Dragging orders changes their display position, never the number.)",
          "An order's Quoted Allowance is calculated — it's the sum of its contract lines' allowances and updates whenever a contract line changes. It can't be typed on the order.",
          "Contract Given and Invoices Received are always computed from the lines, so they can never drift.",
          "An order that has received contractor invoices can't be deleted.",
          "Combine Orders appears when one contractor has several orders with no invoices — it merges them into the contractor's first order.",
          "Overrun badge: TBD until both Contract Given and Quoted Allowance exist · On Target while invoices ≤ contract given · orange while within the quoted allowance · red beyond it.",
          "Markers on the contractor name: red NO PL = the contractor's public-liability insurance has lapsed (or has no expiry recorded) · * = an unapproved contractor invoice is waiting · !* = that approval is currently blocked.",
        ],
      },
      {
        heading: "Contractor invoices",
        rules: [
          "An invoice can only be Approved when the order has Contract Given > 0 AND the H&S paperwork is settled (H&S N/A, or Risk Assessment + Completed Photos uploaded).",
          "A red ! marks an invoice that has an amount but hasn't been approved.",
          "Once a contractor invoice has been rolled into a Do & Charge invoice, its amount is locked until that invoice is voided — our invoice was built from it. Everything else stays editable: the invoice number, the ignored amount, approving it and the comments are the record of the contractor's invoice coming in, and cannot change ours going out.",
          "The Ignored amount is the part excluded when rolling costs into a Do & Charge invoice.",
          "When invoices push an order past its Contract Given, the order joins the Overruns report for a senior check — see the ? on that report.",
        ],
      },
      {
        heading: "Do & Charge",
        rules: [
          "Only available on Do & Charge, Standard Go Ahead and Callout Emergency Works jobs.",
          "Rolls the un-invoiced contractor costs (plus markup, P&G, PM, rubbish, call-out) into a Quoted Invoice — the header text is required.",
          "Markup covers contractor costs + rubbish (never the call-out or PM); P&G is calculated on everything including the PM cost.",
          "Generating also creates an approved variation (“Invoice: N — header”) so the job's value shows in the quote's Grand Totals.",
          "While the generated invoice is unpaid you can Replace it — same invoice number, rebuilt from the current costs and your saved inputs.",
        ],
      },
    ],
  },

  overruns: {
    title: "How Overruns work",
    intro:
      "An overrun is an order whose contractor invoices have grown past its Contract Given. The report is the checking round: overruns appear here automatically, a senior person checks each one, and it falls off the list.",
    sections: [
      {
        heading: "How an overrun gets onto the list",
        rules: [
          "Every time a contractor invoice is saved, the order's overrun (invoices received − contract given) is recalculated.",
          "The order joins the Current Overruns list when the overrun is above zero AND bigger than the last amount that was checked — so a checked overrun only comes back if it GROWS.",
        ],
      },
      {
        heading: "What \"Set to Checked\" does",
        rules: [
          "Administrators only.",
          "The order leaves the Current Overruns list.",
          "The overrun amount is written onto the newest contractor invoice that hasn't been billed yet, as its Ignored amount — so the overrun is excluded from Do & Charge client billing. Any earlier Ignored amounts on the order are replaced.",
          "The checked amount becomes the new baseline: the order only reappears if the overrun grows beyond it.",
          "\"Set all On Target to checked\" clears every listed order whose invoices are back within Contract Given, in one hit.",
        ],
      },
      {
        heading: "The period reports",
        rules: [
          "This Month / Last Month / YTD (April–March) / a picked month.",
          "Historical = the monthly overrun history: each month shows the overrun MOVEMENT recognised that month (checking an order writes/refreshes its history).",
          "Live totals = orders currently over their Contract Given, dated by when they were last checked.",
          "The red total adds up the overruns shown.",
        ],
      },
    ],
  },

  status: {
    title: "How Status & History work",
    intro:
      "A claim moves Requested → Inspected → Quoted → Accepted (→ Completed), or is Declined. " +
      "Every change is recorded as an event — the history below is the complete, permanent audit.",
    sections: [
      {
        heading: "Moving forward",
        rules: [
          "The arrows only ever move FORWARD; to go back, use Roll back.",
          "Accept/Decline can be reached early (Do & Charge-style jobs skip the quote).",
          "Declining always asks for a reason — pick one of the five standard reasons (Withdrawn · Cash Settled · Claim Declined · Non Competitive · Allocated to MPS).",
          "Standard R&Q jobs must have the quote marked Typed AND Checked before they can be Quoted or Accepted — and must have been Quoted before they can be Accepted.",
          "Checked waits for the PC Sum cost types tick: a quote cannot be marked Checked until the typist has ticked that the PC Sum cost types are entered (KS, 8 Sept 2026). Jobs already Checked are not touched.",
          "Completed is the end of the road — nothing moves after it.",
        ],
      },
      {
        heading: "Reinspections (cycles)",
        rules: [
          "A new inspection on a job that's already past Requested opens a NEW cycle — a fresh row in the grid (“Re Requested”). There is no limit on cycles.",
          "Reinspection starts from the Inspection tab, not from a status button.",
          "A DECLINED job is revived the same way: start a new inspection. The decline stays on its row as the permanent record.",
        ],
      },
      {
        heading: "Rolling back",
        rules: [
          "Roll back walks back one status at a time — it never deletes history; the rollback itself is recorded.",
          "The original Requested can never be rolled back.",
          "Rolling back past a reinspection's “Re Requested” deletes that inspection (you'll be warned first).",
          "Deleting an inspection unwinds its whole cycle; newer reinspections must be removed first.",
        ],
      },
    ],
  },

  progress: {
    title: "How Progress entries work",
    intro:
      "Progress entries are the claim's running diary — every call, email and site note, " +
      "flagged so each kind can be filtered into its own view.",
    sections: [
      {
        heading: "Entries & flags",
        rules: [
          "Flags are independent — tick as many as apply. The row colour follows the strongest flag (complaint, then reminder, hidden, important, updates…).",
          "Closure Call Client Signoff is the closure call the INVOICE prints: it says the client signed the finished work off. Plain Closure Call is the everyday flag and drives the Overdue Closure Calls list — tick both on the same entry when the call did both jobs.",
          "A LOCKED entry can only be deleted by an Administrator.",
          "Saving a Client Update entry also stamps the claim's First Client Contact date (first time only) and — on an Accepted job — the Go Ahead Contact date. No separate data entry needed.",
          "Ticking Additional Quote on a NEW entry drops a zero-priced costing line into the quote (“Progress Additional Quote on: date”) ready to be priced.",
          "One file can be attached per entry; attaching again replaces it.",
          "To / From suggest people flagged for progress plus this job's (and all) contractors — free text is always allowed.",
        ],
      },
      {
        heading: "Client Contact",
        rules: [
          "The Client Contact box guides the first-call and go-ahead entries: pick Initial Contact or Go Ahead Contact and a result, and Create Entry writes the note AND stamps the claim's First Contact / Go-Ahead date.",
          "Each type only appears while its date is still blank; once both are recorded the box disappears. Stamping the Go-Ahead date is what clears “Go Ahead Contact Not Completed”.",
        ],
      },
      {
        heading: "Out Of Target Reasons",
        rules: [
          "When a job is over an SLA target, the reason can be set here as well as on the SLA report — staff read it on the progress screen, so it lives in both places.",
          "It uses the same picker as the SLA report: green reasons excuse the overrun (acceptable), red reasons are claims' fault. It's the same reason either way — set it here or there.",
        ],
      },
      {
        heading: "SOW Confirmation",
        rules: [
          "Scope of Works notes marked “needs confirmation” show a warning here until confirmed.",
          "Pick the scope line on an entry and tick Confirmed — the Quote tab's line updates immediately (and the Orders auto-create unblocks).",
        ],
      },
      {
        heading: "Complaints (Adverse Feedback)",
        rules: [
          "An Adverse Feedback entry stays an open complaint until its resolution details and date are filled in.",
          "Open complaints show a banner; the “show complaints first” switch floats every Adverse Feedback entry to the top (resolved ones included, so the full history reads together).",
        ],
      },
      {
        heading: "Invoice Debt Recovery",
        rules: [
          "Only offered when the claim has invoices: link an entry to the invoice being chased.",
          "Linking stamps the invoice's PM-actioned date; ticking External Approval stamps the approval date on the invoice (shown on the Invoices tab).",
        ],
      },
    ],
  },

  officeExpenses: {
    title: "Office Expenses — GPS's own bills",
    intro:
      "A register of the office's supplier bills with the approval trail accounts needs: received, approved, sent for payment, paid. It is not a job and never counts in any job or contractor spend figure. Anyone in the office can see it and add bills; Approve and Sent for payment are for Administrators.",
    sections: [
      {
        heading: "Entering a bill",
        rules: [
          "A bill needs a date, an amount and a supplier — a listed contractor, or a name typed in. Invoice number, category and what-for are optional; the category box suggests what has been typed before.",
          "Amounts are as entered. A refund or credit can be negative.",
          "Notes sit under a bill (sent to accounts, chased, waiting on a credit) or on the register itself for anything general.",
          "The File column holds the bill itself — drop the supplier's PDF or a receipt photo onto the cell, or click it to choose one. Dropping onto a bill that already has a file replaces it; × takes it off. The bills brought across from 1.0 came with the documents that were attached there.",
        ],
      },
      {
        heading: "The trail",
        rules: [
          "Approve stamps who and when — GPS says pay it. Email MYOB then opens a mail to accounts' MYOB importer, the same link as on the Orders tab: attach the bill's PDF and send. Sent for payment stamps when that was done (or the bill was handed to accounts), and that is the end of the trail here: this system is not connected to MYOB, so nobody can mark a bill paid by hand. A Paid stamp appears only where it is genuinely known — the history brought across from 1.0, and MYOB itself once it is connected.",
          "Status is the highest stamp reached, so it always agrees with the dates. Un-approving a bill clears the later stamps too — a bill that is not approved has not been sent.",
          "A bill that has been sent for payment cannot be deleted; press Back first. That is deliberate, so nothing at accounts vanishes in one click.",
        ],
      },
      {
        heading: "Years and the CSV",
        rules: [
          "The year buttons cover the whole register and show each year's count, total and how many are not yet sent for payment; the filters and totals below apply to the year chosen.",
          "Download CSV gives accounts one row per bill with the stamps as dates, for whatever filter is showing.",
          "The bills logged in 1.0 against the office-expenses job from 2023 onward were brought across on go-live, with the notes and documents that went with them. 1.0 never recorded payment, so everything approved before July 2026 came across marked paid on its approval date; the last two months were left to be worked through.",
        ],
      },
    ],
  },
  orderFees: {
    title: "Order Fee Profiles — the Do & Charge panel's presets",
    intro:
      "The Do & Charge Generate Invoice panel is the fee system for orders: markup, P&G, project management, rubbish and call-out. Its RATES are prefilled from data you can edit here; the QUANTITIES (hours, loads) are always typed per job.",
    sections: [
      {
        heading: "Order Fee Profiles — who gets which rates",
        rules: [
          "A profile row = a master company (or one insurer under no master, or neither — the standard row) + a job type (blank = that group's default), carrying the Markup %, P&G %, PM $/hour, Rubbish $/load, Call-out $/hour and the two checkbox defaults.",
          "The most specific row wins outright: group + job type, then group default, then standard + job type, then the standard row. A blank rate on the winning row means the standard rate from Standard rates and wording.",
          "The panel is prefilled when it opens on a job with nothing generated yet. Replacing an unpaid invoice reloads the figures it was generated with instead — \"Reset rates to profile\" puts the presets back.",
        ],
      },
      {
        heading: "Standard rates and wording — the other view on the same tab",
        rules: [
          "One registry per line — Markup, P&G / Overheads, Project Management, Rubbish Removal, Call-out Fee: the standard rate, and exactly what that invoice line says.",
          "A registry switched off leaves that rate blank on the panel (type one, or leave the line out); its wording still applies whenever a figure is entered.",
          "Quantities never have a preset: PM hours, rubbish loads and call-out hours vary job to job (measured across all 696 saved 1.0 panels — rates one number wherever charged, quantities not).",
        ],
      },
    ],
  },
  quoteFees: {
    title: "Automatic P&G & Job Management Fee",
    intro:
      "Two overhead fees the system can add to a quote by itself and keep up to date as the quote changes. Which jobs get them, and at what rates, is decided by data you can edit — not by code.",
    sections: [
      {
        heading: "Quote Markup Profiles — who gets charged what",
        rules: [
          "A profile row = one master company + one job type (blank job type = that master's default), with the P&G %, the JMF %, the Profit % and the default markup for those jobs.",
          "No profile row means NO automatic fees for that combination — those jobs price their overheads inside the markup, which is exactly how most Vero Standard R&Q work is priced today.",
          "The seeded numbers come from measuring recent real quotes, not from guesswork — change them here and every new quote follows.",
          "Master companies themselves (which insurers count as VERO, AA, …) are managed by the system owner under Owner Data.",
        ],
      },
      {
        heading: "Standard rates and wording — the other view on the same tab",
        rules: [
          "The P&G registry's lines are the components the percentage is made of — each with its own percent; the total is their sum. The wording in the total row is exactly what the quote line will say.",
          "The Job Management Fee is always one line: a percent of the job's cost, shown on the quote as hours at the hourly rate (quarter hours allowed).",
          "A registry's \"Fee switched on\" switch is that fee's master OFF switch, everywhere at once.",
        ],
      },
      {
        heading: "On the quote",
        rules: [
          "The fee lines sit pinned at the bottom, badged \"auto\", and recalculate as the quote changes. Edit one and it stops following (badge \"edited\") — Reset puts it back. Delete stays deleted until Restore.",
          "Jobs from before this upgrade have the feature off: their hand-entered fee lines are left exactly as they are. \"Enable automatic fees\" on the Quote Lines toolbar turns it on for that job — nothing old is changed or removed.",
          "A third automatic line — Builders Overheads and Profit — can be switched on per segment in Quote Markup Profiles: a percent of the ordinary lines' sell price, printed with no percent showing. It makes the margin a real line, so the Vero print needs no separate margin row.",
          "The P&G breakdown button shows the client-facing version of the P&G — the client wording scaled to this job's percent, with dollars. Its list lives on the Quote Markup Profiles tab as the P&G client wording and must total the same percent as the internal P&G.",
          "Variations get the same four lines, one set per variation, worked out from that variation's own costing lines at this job's rates — see Variations under How the Quote works.",
        ],
      },
    ],
  },
  quotes: {
    title: "How the Quote works",
    intro:
      "The quote is the source of truth for the job's scope and value. Everything downstream (orders, progress claims, invoices) reads from it.",
    sections: [
      {
        heading: "Money & entering lines",
        rules: [
          "Every priced line: gross = quantity × unit price · markup = gross × % · sell = gross + markup.",
          "New lines start at 57% markup.",
          "A priced line needs a Work Centre and a quantity before it saves — orders and progress claims depend on them.",
          "The Work Centre column dropdown shows each centre's cost before markup. Choose a centre to filter the lines and totals.",
          "After saving a line, the next one keeps the same Floor and Room — enter a whole room in one run.",
          "A pasted link wraps inside its cell instead of stretching the grid off the screen.",
          "Grand Totals = estimates + variations + PC sums — unless a Final Quote exists, which overrides everything.",
          "On Do & Charge-style jobs the Grand Totals count only variations + PC sums; regular estimate lines are working notes there.",
          "A quote line that has been billed on an invoice is locked to that invoice and won't be billed again.",
          "Set Markup (Administrators/Receptionists) re-markups every quote line in one hit — PC Sum lines are excluded.",
        ],
      },
      {
        heading: "P&G and Job Management Fee (automatic lines)",
        rules: [
          "New jobs grow two automatic lines at the bottom of the quote: Preliminaries & General (a percentage of the quote's cost — its makeup is listed in System Data → Quote Markup Profiles) and the Job Management Fee (a fixed percentage shown as hours at an hourly rate).",
          "They recalculate themselves as the quote changes — the badge says \"auto\".",
          "Edit one and it stops following the quote (badge \"edited\") — that's how you trim to win a job. Reset puts it back on automatic.",
          "Delete one and it stays gone until you press Restore.",
          "Either fee can be switched off per insurer in System Data. Jobs from before this feature keep their hand-entered overhead lines and are left alone.",
        ],
      },
      {
        heading: "Printing — the Vero layout",
        rules: [
          "Three print options make the Vero shape: Room section totals (numbered sections, Total for each room), Costs + margin line (line amounts at cost, one Builders Overheads and Profit line showing dollars and the margin percent), and Qty/rate columns.",
          "Vero jobs open the print with those options already ticked; any job can use them, and any switch can be unticked — the totals always come to the same ex-GST figure either way.",
          "The percent on the margin line is margin of the sell price, not markup on cost — 57% markup shows as 36.3%.",
        ],
      },
      {
        heading: "Variations",
        rules: [
          "A variation must have a reason before it can be saved.",
          "Each variation grows the same automatic fee lines as the quote (P&G, JMF, Profit, Rubbish) from its own costing lines, sitting under that variation's lines and badged \"auto\". A costing line with no variation gets none. Edit one and it stops following (Reset puts it back); delete stays gone until Restore.",
          "Variations from before this feature, and the one a Do & Charge invoice creates, have the automatic lines off — \"Enable automatic fees\" on the variation turns them on for that variation only, leaving its existing lines alone.",
          "Only APPROVED variations feed progress claims.",
          "Costing lines that are on an invoice (including Do & Charge generated ones) are frozen — no edit, no delete — and their variation can't be deleted.",
        ],
      },
      {
        heading: "PC Sums & Scope",
        rules: [
          "Clone Room copies a room's lines into another room: pick the room, untick anything that should not carry, retitle the header if needed, choose where it goes and where the copies sit. Automatic fee lines are never cloned.",
          "Delete Room removes every quote line in one room at once — refused whole if any of them is already on an invoice.",
          "A PC Sum that has been claimed on a progress claim is locked.",
          "A PC Sum and its Actual are one unit: deleting either deletes both (refused if the pair has been claimed).",
          "Merge PC Sums can also take lines already pulled — their existing pair is replaced by the merged one, as long as it hasn't been claimed.",
          "Un-ticking a scope line's “needs confirmation” also clears its “confirmed” state.",
          "Unconfirmed scope notes block Auto-create on the Orders tab.",
          "Variations, PC Sums and Final Quote stay hidden until the job is Accepted — unless they already carry rows (data is never hidden).",
        ],
      },
      {
        heading: "Sign-off & printing",
        rules: [
          "The quote must be marked Typed before it can be marked Checked; clearing Typed clears Checked too.",
          "Retyped by me records a revised quote and requires fresh PC Sum confirmation and checking; it returns to the assigned checker's Typed Not Checked list. Rechecked by me records another check. Earlier Typed/Checked marks stay in Sign-off history, even after Clear.",
          "Once Typed, “Check by” assigns WHO should check — the quote then sits in that person's Typed Not Checked list (their “quotes for me to check”). It's routing, not a lock: anyone may still tick Checked. Unassigned quotes fall under the job's Project Co-Ordinator.",
          "Between Typed and Checked, “Waiting on” records why the quote is still to be checked (Contractor to Quote, Insurance Co to Advise, Other…). It clears itself when Checked is ticked, and the RDR's Typed Not Checked summary groups the list by it. The phrases are edited on System Data → Quote Waiting-On Reasons.",
          "Standard R&Q jobs must be Typed AND Checked before the claim can move to Quoted/Accepted.",
          "Opening the quote letter is a PREVIEW and changes nothing; the Print button inside it moves an Inspected job to Quoted (any later status — including Accepted — just prints).",
          "Your Ref on the quote is the assessor's reference, or the insurance claim number when there is none.",
          "A quote header line that carries a description prints like an item: bold title and description inside the description column, no amount. Only a title-only header (GENERAL, LOUNGE) prints as a heading across the page.",
          "The print opens with the options on the left and the document on the right. Pick the DOCUMENT at the top — Quotation, Estimate, Scope of Works, Header sections only, the Room sections layouts (AA / Vero), Internal — and its preset is applied; the sections below adjust that document (headings, money columns, summary, wording, bordered table). Adjust past a preset and the panel says Custom, with a click back. The choices only change how THIS print looks — never the quote itself — and the job remembers how it last went out.",
          "Print Scope of Works prints the works without the quote around them: no addressee, no intro, no prices or totals, no validity note or signature; the scope, the lines and the quote notes stay. It never changes the job’s status, even if you change the document options in that preview.",
          "Quantities can print where the price would be — with the unit (5 m2) or the number alone — on a quote or a Scope of Works, under either heading layout.",
          "Floor & room headings also print the quote's own header lines (bold, above the room), so a job structured by unit or by stage keeps that structure; Quote header sections prints the headers alone.",
        ],
      },
    ],
  },

  invoices: {
    title: "How Invoices work",
    intro:
      "Invoices are frozen snapshots — the bill-to, lines and money are captured at creation and never drift when the quote changes. Numbering is automatic and never reused.",
    sections: [
      {
        heading: "Creating invoices",
        rules: [
          "Quoted Invoice — bills every quote line not yet on an invoice, and locks those lines to it (nothing can be billed twice).",
          "Jobs with a Final Quote get a Final Quoted Invoice instead (one per claim, then Replace).",
          "Progress-claim cycles create their own invoices — a cycle can't be invoiced twice.",
          "Do & Charge jobs are invoiced from the Orders tab.",
          "Excess Invoice — bills the property excess to the CLIENT (no GST); one per claim.",
          "Other / Quote Fee — type the amount EX GST and GST is added on the invoice. The quote fee defaults per JOB TYPE from System Data → Quote Fees (blank job type = every type not listed): $220 + GST = $253 as standard, $500 + GST = $575 for Tender Reinstatement. The button shows the figure; a typed amount still wins.",
          "A Quote Fee or Other invoice has editable LINES in its side panel while it is a draft: the fee stays the fee and a ferry crossing or travel is its own line, ex GST, with its own wording. The totals recalculate from the lines; sending freezes them.",
          "An invoice can never be created with a negative total.",
        ],
      },
      {
        heading: "Completion evidence (per-insurer)",
        rules: [
          "Insurers switched on in System Data get a Completion block on Quoted / Final Quoted invoices: finished photographs plus the closure-call and positive-feedback notes.",
          "The closure line comes ONLY from a Closure Call Client Signoff progress entry — plain Closure Call entries don't print. If none exists, a warning appears beside Print so the invoice isn't sent without the client's call.",
          "Photographs are chosen per invoice; insurers that require them block Mark as Sent until some are selected.",
          "The payment terms print directly under the totals; the Completion block follows them on the page.",
          "Open an invoice and the side panel holds the job's Invoice To lines and Invoice Quote Header (1.0's boxes). When To line 1 is filled, the three lines replace the addressee on every invoice for the job; the header prints above the lines exactly as typed.",
          "Those two are the job's, not the invoice's: one entry prints on every invoice for the job, sent or not.",
          "The Comments column on the invoice list edits in place (the pencil), sent or not — it is the office's own note on the invoice and never prints.",
          "While an invoice is open the browser tab is named invoice number then GPS number (84271 76562), so Save as PDF proposes that file name.",
        ],
      },
      {
        heading: "The excess",
        rules: [
          "The excess is deducted from an insurer invoice only AFTER the Excess Invoice has been paid by the client.",
          "It is deducted exactly once per claim — the first invoice created after the excess is paid carries the deduction.",
          "A progress invoice prints its saved excess deduction and billed total. Later progress claims do not deduct that excess again.",
          "A progress invoice opens the same full document from Invoicing or Progress Claims, with its line breakdown and claim number. It has no FILE LIFE box.",
          "While an Excess Invoice is unpaid, a not-paid reason can be recorded on the row (reports show it orange instead of red).",
        ],
      },
      {
        heading: "Paid, disputed & void",
        rules: [
          "The invoice date starts as today and can be adjusted on the row afterwards.",
          "Any invoice can be marked Final (or un-marked) — a final invoice tells the accounts worklists this job's billing is finished.",
          "Marking paid stamps the payment date; unmarking clears it.",
          "Replace (unpaid invoices only) rebuilds the lines and amount under the SAME invoice number.",
          "Voiding releases everything the invoice billed — the progress-claim cycle, the locked quote lines, the contractor costs and a Do & Charge invoice's generated variation. A paid invoice can't be voided.",
          "A progress claim whose invoice has been paid can't be deleted.",
          "“Will not be invoiced” keeps a job that's never getting an invoice off the not-yet-invoiced worklists.",
        ],
      },
    ],
  },

  details: {
    title: "How Claim Details works",
    intro:
      "The claim's master record. Most fields are plain data entry, but a handful carry rules — " +
      "and several only appear in certain job states or job types.",
    sections: [
      {
        heading: "Accepted status (To Organise / In Progress / On Hold)",
        rules: [
          "Only shown while the job is Accepted. A freshly accepted job starts as To Organise.",
          "A Standard R&Q job can't be marked In Progress until its Actual Start Date is set.",
          "Putting a job On Hold needs a reason (and ideally a review date). Coming OFF hold automatically extends the Estimated End Date by the business days it was held.",
          "Every change writes a progress entry, so the history is on the Progress tab.",
          "Declining or completing a job that's On Hold takes it off hold first.",
        ],
      },
      {
        heading: "Completing a job",
        rules: [
          "A job can only be Completed when it has: an Assessor Ref OR Insurance Claim #, Client Name, Address, Assessor, Insurer/Debtor, and all four start/end dates.",
          "Claim numbers can't be placeholders — \"TBA\", \"to be advised\" or \"unknown\" block completion.",
        ],
      },
      {
        heading: "Dates & business days",
        rules: [
          "Accepting a Standard R&Q / Standard Go Ahead job auto-fills missing estimated dates: start = accepted date + 1 business day (from the excess-paid date when there's a paid excess; Vero jobs always from acceptance), end = start + 30 days.",
          "Business days skip weekends and NZ public holidays. Holidays live in System Data → Public Holidays (seeded to 2032, incl. Matariki) — add future years there, no code change.",
        ],
      },
      {
        heading: "Flags & special fields",
        rules: [
          "Problem Job stamps the date it was set; only an Administrator can un-set it.",
          "Callout jobs carry a Quote Request state — it shows red until QR Received or QR Not Required is chosen.",
          "Property Excess turns red while an Accepted/Completed job's excess is unpaid (orange when a not-paid reason is recorded); it shows (Paid) once the excess invoice is paid. Paid state comes from the Invoicing tab.",
          "Job Type is required — a claim can't be created without one, and it can never be cleared (only changed).",
          "Choosing the Tender Reinstatement job type forces the Tender division and reveals the Tender Due Date.",
          "Assessor Routing (Quotes/Updates/Variations/Invoices To) defaults to the assessor — only set it when a different contact should receive that paperwork.",
          "Division, Job Type and Property Excess can only be changed by Administrators/PowerUsers.",
        ],
      },
      {
        heading: "The ⓘ quick facts (every tab)",
        rules: [
          "The ⓘ sidebar edits Lockbox, RDR Note and Job Summary from any tab — one save path, and Details stays the home for everything else.",
          "Click the PCO name to email the assigned coordinator with the job number and works address in the subject, as in 1.0. A name without an email address stays plain text.",
          "Pin the panel to open it beside the job at its compact width. Drag it wider if needed; your pin and adjusted width are remembered across jobs and logins while pinned. Drag its right edge to resize, or focus the edge and use arrow keys; Reset width restores the starting width.",
          "On narrower windows it opens over the page instead. Labels adapt as you resize, and full labels are available in the hints. Closing or unpinning closes the panel. While pinned beside the job, duplicate client/insurer/job-type details are hidden from the tab rail, and status stays in the tab rail.",
          "It also carries switches for Important Job, Problem Job and Contractor Quoting. The same rules apply as on Details — un-ticking Problem Job still needs an Administrator.",
        ],
      },
    ],
  },

  pow: {
    title: "How the Program of Works works",
    intro:
      "Two lists live side by side: the CALENDAR of dated, contractor-assigned tasks, and the SCHEDULE checklist — the ordered to-do list (PRE-START, SOW sent, …) with no dates.",
    sections: [
      {
        heading: "The calendar",
        rules: [
          "Click a day to add a task; click a task to edit it; drag a task to reschedule it. Weekends are hidden.",
          "The contractor dropdown offers this job's ORDER contractors — set the orders up first.",
          "“Display by” switches what each event shows (title / contractor / description); Hide Completed clears the noise.",
        ],
      },
      {
        heading: "The schedule checklist",
        rules: [
          "An ordered list of steps — drag to reorder, tick to complete (the completed date stamps automatically).",
          "Checklist items carry no dates — dated work belongs on the calendar.",
        ],
      },
    ],
  },

  progressClaims: {
    title: "How Progress Claims work",
    intro:
      "Progress claims bill the job in %-complete cycles against the quote: estimates (grouped by work centre), PC sums, and APPROVED variations.",
    sections: [
      {
        heading: "The % engine",
        rules: [
          "Each line: previously claimed = amount × the % locked in by earlier cycles · this cycle = the new % minus that.",
          "A follow-up cycle starts every line at the prior cycle's %, so nothing is ever billed twice.",
          "Only APPROVED variations appear; a PC Sum claimed on a cycle is locked on the Quote tab.",
          "Lines are generated by the engine — they can't be added, removed or reordered by hand.",
          "Set-all-100 fills the final cycle in one hit; Refresh re-pulls quote changes into an unfinished cycle.",
        ],
      },
      {
        heading: "Cycles & invoices",
        rules: [
          "Finishing a cycle locks it; each cycle creates its own invoice on the Invoices tab (a cycle can't be invoiced twice).",
          "A cycle whose invoice has been PAID can't be deleted; void the invoice first if it was a mistake.",
          "Double-billing gate: a job with an as-quoted invoice can't start progress-claim cycles, and a job with cycles can't create an as-quoted invoice.",
        ],
      },
    ],
  },

  files: {
    title: "How Files work",
    intro: "The claim's photo and document gallery. Files are stored once, compressed once, and never silently lost.",
    sections: [
      {
        heading: "Uploads & storage",
        rules: [
          "Images are resized/compressed ONCE on the way in and get a thumbnail for the gallery — originals aren't re-compressed later.",
          "Files brought across from the old system carry their names and details; where the actual bytes weren't migrated the gallery shows a placeholder instead of an error.",
          "Any file can be renamed from its tile (the pencil). The name is yours; the extension (.pdf, .jpg) stays with the file so it always downloads as what it is.",
          "Deleting removes the file from the claim (an Administrator can reset a claim's files entirely).",
        ],
      },
      {
        heading: "Where files attach",
        rules: [
          "This tab is the general gallery. Progress entries attach ONE file each (on the Progress tab); QCC inspections keep their own two collections (on the QCC tab).",
        ],
      },
    ],
  },

  qcc: {
    title: "How QCC inspections work",
    intro:
      "Quality Control Check inspections on the job: booked → inspected → PM checked. A claim can carry many; the tab appears once the job is Accepted (or Completed/Declined).",
    sections: [
      {
        heading: "Lifecycle",
        rules: [
          "A QCC is booked once it has a date and an inspector (from the Inspector group); until then it shows as New and does not count on the QCC Inspections Booked list. Add notes and photos for the inspector as needed.",
          "Tick Inspected once the visit has happened (it needs the booking first): the visit's answers, findings, notes, result photos and the Result switches open up. The switches save straight away.",
          "A new QCC can't be added until the current one is complete (date, Inspected, PM Checked).",
          "Only an untouched QCC (not inspected, not PM-checked, no files, no findings) can be deleted.",
          "PM Checked is the office's sign-off; the inspector never sets it.",
        ],
      },
      {
        heading: "Findings and the result",
        rules: [
          "A finding is one thing wrong, recorded against a trade or a section check (Interior / Exterior). Only problems are recorded — trades that were fine are not listed.",
          "No findings means the job passed. Any finding is a fail: adding one marks the QCC Failed. The tick stays editable for a QCC typed up from paper.",
          "The visit's answers sit on the card: stage of completion (25/50/75/100%), met the client on site, another visit required, and client notes. Blank means not answered.",
          "Findings carried over from the old inspector app are marked (inspector app).",
        ],
      },
      {
        heading: "Files",
        rules: [
          "Two separate collections per inspection: the inspection RESULTS, and photos FOR the inspector.",
          "A result photo can be attached to one finding (the Photo button on the finding); it stays on the QCC if the finding is removed.",
        ],
      },
    ],
  },

  // ---- Report pages -------------------------------------------------------

  feedback: {
    title: "How the Feedback reports work",
    intro:
      "Adverse and Positive Feedback are progress entries with the matching flag — these reports list them across all jobs for a date window.",
    sections: [
      {
        heading: "The lists",
        rules: [
          "Rows are the flagged progress entries, filtered by date window (on the entry date), insurer (master groups supported), assessor company, assessor and job type; Hide Tender available. First 1000 shown.",
          "Adverse rows show Days To Resolve (resolution date − entry date); an unresolved complaint shows a red “Not Resolved”.",
          "Positive rows can be Removed from the list while you build a clean print list — Reset brings them back. (Removing never touches the claim.)",
          "Show Columns picks which columns display AND print — remembered per browser, separately for each report.",
          "Highlight marks an assessor company's or insurer's rows in a colour (also remembered) — highlights print, filters and buttons never do.",
        ],
      },
      {
        heading: "Where the data is maintained",
        rules: ["The flags and resolution details live on the progress entry itself — fix the data there, not here."],
      },
    ],
  },

  importantJobs: {
    title: "How Problem & Important Jobs works",
    intro: "All jobs flagged Important and/or Problem, in one list.",
    sections: [
      {
        heading: "The list",
        rules: [
          "Modes: Important / Problem / Both; filters by Project Co-Ordinator and division; sorted by PCO then job number.",
          "The flags are set on the claim (Details Flags, or the ⓘ quick flags on any tab).",
        ],
      },
    ],
  },

  strikeRate: {
    title: "How Strike Rate works",
    intro: "Win rate per month: of the jobs requested that month, how many were accepted vs declined.",
    sections: [
      {
        heading: "The numbers",
        rules: [
          "Jobs bucket by the month they were REQUESTED. Undetermined = still Requested/Inspected/Quoted; Accepted counts Accepted + Completed; Declined splits by the five decline reasons.",
          "Strike rate % = accepted ÷ (accepted + declined) — undetermined jobs don't count against you.",
          "Filters: month range (default last 12), insurer (incl. master groups), assessor, assessor company, division, and “Jobs quoted only”.",
          "Breakdown tables repeat the same numbers by assessor, insurer, assessor company and division, each with totals and an overall rate.",
        ],
      },
    ],
  },

  jobSchedule: {
    title: "How Job Schedule works",
    intro: "The live whereabouts of every Accepted job, in six buckets.",
    sections: [
      {
        heading: "The buckets",
        rules: [
          "In Progress · On Hold (reason shown red) · To Organise · Problem · Important · Callout In Progress. Up to 150 per bucket, earliest start date first.",
          "The Project Co-Ordinator filter defaults to YOU.",
          "Rows click through to the claim — dates and status are edited on the claim itself (Details / Status), Lockbox and Summary in the ⓘ quick facts.",
        ],
      },
    ],
  },

  nonApprovedInvoices: {
    title: "How Non Approved Contractor Invoices works",
    intro: "Every contractor invoice with an amount that hasn't been approved yet, across all jobs.",
    sections: [
      {
        heading: "The list",
        rules: [
          "One row per unapproved invoice; filters by division, PCO, job type, contractor, and an optional created-after date.",
          "The contractor name is a mailto link to the order's contractor contact; each row shows the order's received total beside this invoice; totals in the footer.",
          "Approval happens on the Orders tab (see the approval gate in the Orders ? help).",
        ],
      },
    ],
  },

  phonebook: {
    title: "How the Phonebook works",
    intro: "The contractor/assessor/insurer directory, built from System Data.",
    sections: [
      {
        heading: "Contractors",
        rules: [
          "Active contractor companies with their trades, star rating, PL expiry (red once past), contact people (primary marked) and notes.",
          "Filter by contractor, work centre (trade) or minimum rating.",
        ],
      },
      {
        heading: "Email lists & upkeep",
        rules: [
          "Tick rows to build ONE email to every selected address (addresses deduped).",
          "A contractor company's documents — public liability, health & safety, pre-qualification — live on its System Data editor: one current file per type, drop to add or replace, H&S and pre-qualification with their own expiry, PL expiry on the company field.",
          "Ratings, trades, contacts, PL expiry and notes are maintained on the company editors in System Data — Administrators can click straight through from a row.",
        ],
      },
    ],
  },

  performance: {
    title: "How the Performance Report works",
    intro:
      "Who did what, per period: This Week (from Sunday), This Month, Last Month, or the April financial year to date.",
    sections: [
      {
        heading: "The by-person table",
        rules: [
          "Quotes Typed / Checked count each recorded sign-off, including repeats; Positive and Adverse Feedback are credited to the job's Project Co-Ordinator; Marketing Calls to whoever logged them.",
          "Badge Completions is the 2.0 measure: how many needs-attention items the person CLEARED — with the by-rule breakdown underneath showing exactly what kind of work it was.",
          "Administrators see everyone; everyone else sees only their own row.",
        ],
      },
      {
        heading: "Also here",
        rules: [
          "The QCC strip: inspections done vs jobs accepted in the period, and the pass rate.",
          "Declined jobs by reason for the period.",
        ],
      },
    ],
  },

  orderWorklists: {
    title: "How the order worklists work",
    intro:
      "Three views over the same order grid: H&S Order Worklist (paperwork missing), Contractors Report (everything, filter as needed) and Contractors Complete Needs Checking (paperwork done, invoices in, but work not ticked complete).",
    sections: [
      {
        heading: "What's listed",
        rules: [
          "Rows are ORDERS on Accepted jobs (tick “Include recently completed” to add jobs completed in the last 3 months).",
          "H&S Order Worklist: orders with invoices received whose Risk Assessment or Completed Photos are still outstanding (red YES) — unless marked H&S N/A.",
          "Contractors Complete Needs Checking: paperwork complete and invoices received, but Contractor Work Complete not ticked — go confirm the work is done. Bookkeeping “contractors” (Overheads, Materials, Rubbish) are excluded.",
          "First 250 rows shown — narrow the filters.",
        ],
      },
      {
        heading: "Working the list",
        rules: [
          "Comments and the Work Complete tick save straight onto the order (the Save button lights up when a row has changes) — the same fields you'd edit on the job's Orders tab.",
          "CSV downloads the current list for Excel.",
          "The contractor's ✉ opens an email to them; Job # opens the job's Orders tab.",
        ],
      },
    ],
  },

  findInvoice: {
    title: "How Find Invoice works",
    intro: "Look up any invoice by number, job, insurance claim number, type or date — and trace contractor bills too.",
    sections: [
      {
        heading: "Searching",
        rules: [
          "Fill any mix of filters — invoice number range, job number, insurance claim number (starts-with), type, date range.",
          "The page remembers your last search in this tab — filters, the Invoices / Contractor invoices choice and the results — so coming back from an invoice puts you where you were. Clear forgets it.",
          "Narrow by the job's Insurer, Current Assessor, Assessor Co, Project Co-Ordinator or Division; Paid and Approved are Yes / No / All, and the Approved date range reads the assessor's approval date.",
          "The contractor invoice # box switches the search to CONTRACTOR invoices — find whose bill it is, which order it's on, and whether it's approved.",
          "Rows click through to the job's Invoicing (or Orders) tab. First 250 shown, newest first.",
          "Export CSV first asks which columns to include (1.0's Hide Columns, asked at export time) — your ticks are remembered for next time.",
        ],
      },
    ],
  },

  findOrder: {
    title: "How Find Order works",
    intro: "Look up any order by its Order # — the same numbering family as claims and invoices.",
    sections: [
      {
        heading: "Searching",
        rules: [
          "Search by order number range, job number, or contractor; rows show Contract Given and Invoices Received and click through to the job's Orders tab.",
          "First 250 shown, newest first.",
        ],
      },
    ],
  },

  kpiReport: {
    title: "How the KPI Report works",
    intro:
      "The monthly pack for one assessor company: SLA compliance, File Life averages and positive feedback on one page. It assembles what used to be collected by hand out of three separate screens.",
    sections: [
      {
        heading: "Who and when",
        rules: [
          "One pack per ASSESSOR COMPANY (AA, McLarens, Sedgwick, Vero…), not per insurer — two of the companies GPS reports to are only ever assessor companies.",
          "Pick “All companies” to see the whole month across everyone: GPS's own view, and the honest denominator behind the all-insurer lines.",
          "Year to date runs 1 January to the end of the month you picked.",
        ],
      },
      {
        heading: "Where each figure comes from",
        rules: [
          "SLA: Standard R&Q jobs LOGGED in the month, excluding quick declines and jobs accepted the day they were logged with no inspection booked. Exactly the SLA report's population, so the two always agree.",
          "The three lines are the SLA report's own columns: Logged → First Contact (1 day), First Contact → Scheduled Inspection (3 days), and Job Inspected → Quote Sent (2 days).",
          "File Life: business days Accepted → Completed less non-GPS-fault hold days, counted in the month the job was COMPLETED. Same definition as the File Life report.",
          "Positive feedback: progress entries flagged as positive, on the date they were entered.",
        ],
      },
      {
        heading: "Why it may not match an older pack",
        rules: [
          "Every percentage shows the population behind it (“57 of 64 jobs”), so a figure sent to an insurer can still be explained a year later.",
          "A band with no completed jobs says so instead of printing a number — “0.00 days” would be a claim about performance that nobody made.",
          "A completed job with nothing invoiced is shown as NOT BANDED rather than counted as under $30K, because hiding it there would move the average with no way to tell.",
          "The $30K split uses the total invoiced on the job, excluding voided invoices. Nothing in ClaimsDesk 1.0 or 2.0 ever defined that band, so this is GPS's choice and can be changed — see docs/business-rules/kpi-report.md.",
        ],
      },
    ],
  },

  fileLife: {
    title: "How File Life works",
    intro:
      "File Life = the business days a job took from Accepted to Completed, minus hold time that wasn't GPS's fault. The allowance is 25 days.",
    sections: [
      {
        heading: "The calculation",
        rules: [
          "Business days Accepted → Completed, minus the days of on-hold episodes whose reason isn't GPS's fault (client, assessor/insurer, awaiting excess…).",
          "Contractor, CCC and Christmas holds count as GPS's fault — no credit for those.",
          "Every invoice prints the job's FILE LIFE stamp (red box) so the insurer sees it.",
          "The SLA report carries a File Life column — only completed jobs get a value.",
        ],
      },
    ],
  },

  predictiveInvoicing: {
    title: "How Predictive Invoicing works",
    intro:
      "The cashflow forecast: for every Accepted job with quote value still to bill, when is the money expected?",
    sections: [
      {
        heading: "The forecast",
        rules: [
          "Remaining to bill = the quote's value (Final Quote when one exists) minus what's already invoiced — recalculated LIVE, so the forecast can never exceed what's actually left.",
          "Each expected-invoice date contributes its % of the remaining amount to its month's column.",
          "Buckets: Overdue (expected dates already past) · Not Set (no expected dates — 100% of remaining, the “go set your dates” nudge) · the coming months · On Hold (parked jobs, kept out of the live months).",
          "Expected dates are entered on the claim's Invoices tab: a start date + a % split, or edit the rows individually.",
        ],
      },
    ],
  },

  invoicingByMonth: {
    title: "How Invoicing by Month works",
    intro:
      "What actually went out the door: invoiced totals per calendar month.",
    sections: [
      {
        heading: "The numbers",
        rules: [
          "An invoice counts in the calendar month of its INVOICE DATE. Voided invoices never count; whether it's been paid is irrelevant.",
          "Each month shows the invoice count, how many distinct claims were billed, Subtotal (ex GST), GST, Total and the average per invoice — with a grand-total footer. Hover a month for its per-type split.",
          "The current month is flagged “partial” — more invoices are still to come.",
          "Excess invoices (billed to the client, not the insurer) are included by default; flick the switch off to see the insurer-revenue picture alone.",
          "Quick ranges (Last 3/6/9/12 months) fill the month pickers; editing a picker switches to Custom. The report re-runs itself as you change anything.",
          "Filters match Predictive Invoicing: division, project co-ordinator, job type and insurer (combined insurer groups included).",
          "“Show invoices” lists every invoice under its month with a link to the claim.",
        ],
      },
    ],
  },

  jobsLogged: {
    title: "How Jobs Logged / Accepted works",
    intro: "The office favourite: weekly logged/accepted counts against target, with history.",
    sections: [
      {
        heading: "The numbers",
        rules: [
          "52 Monday–Sunday weeks; each week belongs to the month holding most of its days. The weekly target is 50 logs; +/- is logged minus target.",
          "Logged = the job's first Requested date; Accepted = its first Accepted date.",
          "Monthly totals roll up the weeks; the three prior years ride alongside for comparison.",
          "The Totals target is pro-rated to weeks that have started (1.0 compared part-year actuals against the full 52-week target, which overstated the shortfall all year). Yearly totals for prior years are unaffected.",
          "Months that haven't started yet aren't shown; the in-progress week is highlighted. The headline band's graph is optional — the ▾ arrow shows it.",
        ],
      },
    ],
  },

  qccReport: {
    title: "How the QCC Inspection Report works",
    intro: "All QCC inspections that have BEEN inspected, across jobs.",
    sections: [
      {
        heading: "The list",
        rules: [
          "Only inspected QCCs appear (booked-but-not-done ones don't). Result = Pass unless marked Failed.",
          "Default date range = last month; filter by result.",
          "Inspections themselves are managed on the claim's QCC tab.",
        ],
      },
    ],
  },

  marketingCalls: {
    title: "How Marketing Phone Calls works",
    intro:
      "Relationship upkeep: a subscription list of assessors who should hear from GPS every week, and the log of every call/email.",
    sections: [
      {
        heading: "The weekly chase",
        rules: [
          "The house list is shared; personal lists are supported too. An assessor is DUE when they've had no phone contact this week (weeks start Sunday).",
          "Logging a call or email records who made it automatically — it's always the signed-in user.",
          "The due counter shows on the Dashboard; logging a call for a due assessor clears them for the week.",
        ],
      },
    ],
  },

  completions: {
    title: "How Completions works",
    intro: "A record of needs-attention items being CLEARED: when each badge went away, on which claim, and who did the work.",
    sections: [
      {
        heading: "The list",
        rules: [
          "A row appears when a save clears a badge (the claim stopped matching that rule).",
          "Recording started when the feature shipped — there's no back-history before that.",
        ],
      },
    ],
  },

  // ---- RDR sections -------------------------------------------------------

  workflow: {
    title: "How Workflow Management works",
    intro:
      "The quote-pipeline chase: each line is one rule — a count, and Show Me lists the jobs behind it. Fix the job and it leaves the list.",
    sections: [
      {
        heading: "The lines",
        rules: [
          "From first contact to quote follow-up: No Initial Contact · No Appointment Made · Inspections To Confirm · Outstanding Inspections · Inspections To Accept · Inspected Not Typed · Typed Not Checked · Checked Ready To Send · Quote Follow Up (5 working days) · Contractor Quoting Follow Up · Declined Without Reasons · Declined Need To Invoice · the Tender to-price/quoted queues.",
          "Inspections To Accept only appears when the Inspector Workspace is switched on. The inspector finishing a job on their phone records the visit; it does not move the claim. This line is how the office sees a finished job waiting — open it, check the photos, and Accept Inspection moves it to Inspected and clears the line.",
          "Filters: Project Co-Ordinator (Me / Me + my team / All / Other) and Division; “Only Reports With Records” hides the zero lines.",
          "Lists cap at 200 rows; badges on these lists show only workflow-category items.",
        ],
      },
    ],
  },

  sla: {
    title: "How the SLA report works",
    intro:
      "Working-day turnaround between milestones, per job, against GPS's targets — with the out-of-target excuse system built in.",
    sections: [
      {
        heading: "The columns",
        rules: [
          "Logged → First Contact (1 day, 100%) · First Contact → Scheduled Inspection (3 days, 90%) · Scheduled → Inspected (1 day, 90%) · Inspected → Typed (1 day, 90%) · Typed → Checked (1 day, 90%) · Checked → Sent (same day, 100%) · TOTAL Job Quoted Turnaround, Inspected → Sent (2 days, 90%).",
          "The TOTAL column self-calculates from the steps before it and takes no reason of its own — put the reason on the step that ran over. File Life has its own report.",
        ],
      },
      {
        heading: "The colours",
        rules: [
          "In target = plain (acceptable). Out of target with NO reason = orange (unacceptable). Out of target with a reason that isn't GPS's fault = green (excused). A GPS's-fault reason = red.",
          "The footer % counts acceptable ÷ valid jobs; the total column's allowance grows by the days of any excused overrun.",
          "Each Out-Of-Target reason belongs to one or more steps: a step's picker lists its own reasons first and every other reason below a divider, so the full list is always there. A reason placed on no step sits in the first group everywhere. The catalogue is managed on the Owner Data page.",
          "Reasons are entered on the report itself and stay with the job.",
        ],
      },
    ],
  },

  rdrMain: {
    title: "How the PCO Dashboard works",
    intro:
      "The Project Co-Ordinator's daily round: every line is one rule with a count and a Show Me list. Clear the job, clear the line.",
    sections: [
      {
        heading: "The header",
        rules: [
          "The readouts and pipeline bars stay visible even when the reports switch is off — every bar and stat clicks through as a search filter.",
          "Jobs Logged/Accepted vs targets (10 today · 50 week · 200 month), the SLA compliance strip, and marketing calls due (when marketing is on).",
          "The Claims logged by day chart shows each week's total beside its Monday date. It adds the displayed daily bars, including weekends; the current week runs through the chart's end date.",
        ],
      },
      {
        heading: "The lines",
        rules: [
          "Client updates (5 business days) · daily reviews · on-hold reviews due / missing reasons · awaiting excess · go-ahead contact · To Organise overdue (5 days) · overdue start/end dates · closure calls (3 months) · lockbox pickups · unresolved adverse feedback · assessor updates (per-company thresholds) · QCC booked/to-check · callout jobs needing a quote request (finished within 90 days) · Draughting & Permits.",
          "Problem and Important jobs stay listed WHATEVER their status until they get a review entry today — or the flag is taken off. The flag is the worklist.",
          "Completed Jobs Not Finished and Declined Jobs Not Finished sit together at the foot: closed jobs not yet put to bed. A row with badges needs that work done first; a row with none is ready and wears a Set To Finish chip.",
          "Filters: PCO (Me / Me + team / All / Other) and Division. Lists show the rule's badge + a +N overflow.",
          "Options changes the Attention column between Needs Attention badges (default) and an editable RDR note, just like Claim Search. It applies to Detail lists and Summary drilldowns without changing the jobs or counts. This choice is remembered separately from Search.",
          "Show: All Detail (the rule lines) or All Summary — the 1.0-style grid: one table per rule, a column per person with records, each count clicking through to that person's jobs. On Hold To Review splits by hold reason; No Daily Review splits by job type. The choice is remembered per section, and the same toggle lives on Accounts, H&S and Workflow Management.",
          "How these pages OPEN on a fresh login (coordinator, view, layout — and the Search page's landing filters) is set under Page defaults in the user menu; any filter you change while working is remembered for the session instead. Saving new defaults clears those remembered filters, so they apply straight away.",
        ],
      },
    ],
  },

  accounts: {
    title: "How the Accounts report works",
    intro: "The money round: every line is one rule with a count, a dollar total, and the jobs behind it.",
    sections: [
      {
        heading: "The lines",
        rules: [
          "Overruns To Be Approved mirrors the Overruns report: orders flagged for checking that are still over — checking one clears it here too.",
          "Non Invoiced Jobs = completed jobs with no FINAL invoice (ordinary invoices don't close a job's billing) — with a completed-date range filter.",
          "Orders Yet To Be Invoiced = Do & Charge-style jobs already invoiced once, still carrying contractor costs not rolled into an invoice (completed jobs count for 3 months).",
          "Also: Non Approved Contractor Invoices · Disputed Invoices · Excess Invoices Unpaid / To Raise · Expected Invoice Dates missing · WIP · Overdue Payments (5 business days; red variant when not chased) · Debt Collector (10 days) · PC Sum Actuals to enter · Unprofitable Jobs.",
          "Every money line carries its $ total; “Will not be invoiced” jobs stay off the non-invoiced lines.",
          "The header shows this month's invoice totals by division (Quote Fees split out, dated by invoice date) and a Monthly History button — 12 months of invoice + quote-fee totals.",
        ],
      },
    ],
  },

  whosOnline: {
    title: "Who's online, and how long people worked",
    intro: "System owner only. Nothing here is shown to anyone else — no green dot.",
    sections: [
      {
        heading: "How it is measured",
        rules: [
          "Every request the app makes names its user, so the server notes who, when and which page — in memory, no database write per request. Once a minute that folds into one row per person per day.",
          "Online = seen in the last 5 minutes; idle = within 30; away = older. The list is everyone seen today since the server last started.",
          "Active minutes = five-minute slots of the day with at least one request. Reading a quote or typing a long note for twenty minutes shows as one or two slots — it shows who was here, not a timesheet.",
          "The day view answers 'can I deploy now?' and the old question of who was actually working from home; use it for the first, and for the second only as a prompt to ask.",
        ],
      },
    ],
  },

  healthSafety: {
    title: "How the Health & Safety report works",
    intro: "Two H&S rules over the orders, plus the insurance early-warning list.",
    sections: [
      {
        heading: "The lines",
        rules: [
          "Orders With Overdue H&S: invoiced orders missing their Risk Assessment or Completed Photos (unless marked H&S N/A), on jobs active in the last 3 months.",
          "A contractor company ticked H&S N/A in System Data has H&S N/A forced on every order saved for it, so its orders never appear here — 1.0's rule.",
          "Jobs With Contractors Without PL: open jobs using a contractor whose public-liability insurance has lapsed.",
          "Both Show Me lists name the offending contractor(s) on each row.",
          "The Company PL Expiry Alert lists companies whose PL expires within a month (red once past), with their notes — expiry dates and notes are maintained on the company editors.",
        ],
      },
    ],
  },
};

export default BUSINESS_RULES;
